/***********************************************
 *  EMISSION OVERVIEW COMPONENT – FULL UPDATED
 ***********************************************/
import { DatePipe } from '@angular/common';
import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { getmonths, getShipNameOptions, gettypeofreport, getvesselname } from 'src/app/shared/helpers/dropdown-options';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { OverlapService } from 'src/app/shared/service/overlapService/overlap.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { ColumnFilterPopupComponent } from '../../databasetab/column-filter-popup/column-filter-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { DEMO_VESSEL_PC } from 'src/app/shared/helpers/heating-table-dynamic-extract.util';

@Component({
  selector: 'app-emission-overview',
  templateUrl: './emission-overview.component.html',
  styleUrls: ['./emission-overview.component.scss']
})
export class EmissionOverviewComponent implements OnInit, OnDestroy {

  /*******************************************
   * Interfaces for grouped column structure
   *******************************************/
  interfaceGroupColumn = class {
    key!: string;
    label!: string;
  };

  interfaceColumnGroup = class {
    key!: string;
    label!: string;
    visible!: boolean;
    columns!: { key: string; label: string }[];
  };

  /*******************************************
   * ANGULAR MATERIAL + FORM
   *******************************************/
  @ViewChild(MatSort) sort!: MatSort;

  myreportForm: FormGroup;
  monthOptions: any;
  vesselNameSuggestions: any[] = [];

  paginatedData: any[] = [];
  elements: any[] = [];
  currentPage: number = 0;
  itemsPerPage: number = 100;

  isLoading: boolean = false;

  shipnameoptions: any;
  vesselnameoptions: any;
  typeofreportoptions: string[];
  private destroy$ = new Subject<void>();
  subusername: any;

  activeFilter: string = '';
  selectedClient: string = 'ALL';
  allElements: any[] = [];

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private commonService: CargoheatingCommonService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private overlayService: OverlapService,
    private requestFormService: RequestFormService,
    private dialog: MatDialog
  ) {

    /*******************************************
     * FORM VALUES
     *******************************************/
    this.myreportForm = this.fb.group({
      vesselname: [''],
      typeofreport: [''],
      from_date: [''],
      to_date: [''],
      selectedYear: [new Date().getFullYear(), Validators.required],
      vesselIMO: ['', [Validators.pattern('^[0-9]*$')]],
    });

    this.monthOptions = getmonths();
    this.shipnameoptions = getShipNameOptions();
    this.vesselnameoptions = getvesselname();
    this.typeofreportoptions = gettypeofreport();

    this.updateColumnGroups(this.myreportForm.get('selectedYear')?.value);
  }

  updateColumnGroups(year: number) {
    const yr = year;
    this.columnGroups = [
      {
        key: 'eeoi',
        label: 'EEOI (g/mt × nm)',
        visible: true,
        expanded: false,
        columns: [
          { key: `eeoi_q1`, label: `${yr} Q1` },
          { key: `eeoi_q2`, label: `${yr} Q2` },
          { key: `eeoi_q3`, label: `${yr} Q3` },
          { key: `eeoi_q4`, label: `${yr} Q4` },
          { key: `eeoi_total`, label: `${yr} Total`, isTotal: true }
        ]
      },
      // {
      //   key: 'co2int',
      //   label: 'CO₂ Intensity Displacement (g/mt × nm)',
      //   visible: true,
      //   expanded: false,
      //   columns: [
      //     { key: `co2_intensity_displacement_q1`, label: `${yr} Q1` },
      //     { key: `co2_intensity_displacement_q2`, label: `${yr} Q2` },
      //     { key: `co2_intensity_displacement_q3`, label: `${yr} Q3` },
      //     { key: `co2_intensity_displacement_q4`, label: `${yr} Q4` },
      //     { key: `co2_intensity_displacement_total`, label: `${yr} Total` }
      //   ]
      // },
      {
        key: 'co2e',
        label: 'CO₂e (mt)',
        visible: true,
        expanded: false,
        columns: [
          { key: `co2e_mt_q1`, label: `${yr} Q1` },
          { key: `co2e_mt_q2`, label: `${yr} Q2` },
          { key: `co2e_mt_q3`, label: `${yr} Q3` },
          { key: `co2e_mt_q4`, label: `${yr} Q4` },
          { key: `co2e_mt_total`, label: `${yr} Total` }
        ]
      },
      {
        key: 'pm10',
        label: 'PM10 (kg)',
        visible: true,
        expanded: false,
        columns: [
          { key: `pm10_kg_q1`, label: `${yr} Q1` },
          { key: `pm10_kg_q2`, label: `${yr} Q2` },
          { key: `pm10_kg_q3`, label: `${yr} Q3` },
          { key: `pm10_kg_q4`, label: `${yr} Q4` },
          { key: `pm10_kg_total`, label: `${yr} Total` }
        ]
      },
      {
        key: 'pm10_intensity',
        label: 'PM10 Intensity (mg/mt × nm)',
        visible: true,
        expanded: false,
        columns: [
          { key: `pm10_intensity_q1`, label: `${yr} Q1` },
          { key: `pm10_intensity_q2`, label: `${yr} Q2` },
          { key: `pm10_intensity_q3`, label: `${yr} Q3` },
          { key: `pm10_intensity_q4`, label: `${yr} Q4` },
          { key: `pm10_intensity_total`, label: `${yr} Total` }
        ]
      },
      {
        key: 'ch4',
        label: 'CH4 (kg)',
        visible: true,
        expanded: false,
        columns: [
          { key: `ch4_kg_q1`, label: `${yr} Q1` },
          { key: `ch4_kg_q2`, label: `${yr} Q2` },
          { key: `ch4_kg_q3`, label: `${yr} Q3` },
          { key: `ch4_kg_q4`, label: `${yr} Q4` },
          { key: `ch4_kg_total`, label: `${yr} Total` }
        ]
      },
      {
        key: 'ch4_intensity',
        label: 'CH4 Intensity (mg/mt × nm)',
        visible: true,
        expanded: false,
        columns: [
          { key: `ch4_intensity_q1`, label: `${yr} Q1` },
          { key: `ch4_intensity_q2`, label: `${yr} Q2` },
          { key: `ch4_intensity_q3`, label: `${yr} Q3` },
          { key: `ch4_intensity_q4`, label: `${yr} Q4` },
          { key: `ch4_intensity_total`, label: `${yr} Total` }
        ]
      },
      {
        key: 'co',
        label: 'CO (kg)',
        visible: true,
        expanded: false,
        columns: [
          { key: `co_kg_q1`, label: `${yr} Q1` },
          { key: `co_kg_q2`, label: `${yr} Q2` },
          { key: `co_kg_q3`, label: `${yr} Q3` },
          { key: `co_kg_q4`, label: `${yr} Q4` },
          { key: `co_kg_total`, label: `${yr} Total` }
        ]
      },
      {
        key: 'n2o',
        label: 'N₂O (kg)',
        visible: true,
        expanded: false,
        columns: [
          { key: `n2o_kg_q1`, label: `${yr} Q1` },
          { key: `n2o_kg_q2`, label: `${yr} Q2` },
          { key: `n2o_kg_q3`, label: `${yr} Q3` },
          { key: `n2o_kg_q4`, label: `${yr} Q4` },
          { key: `n2o_kg_total`, label: `${yr} Total` }
        ]
      },
      // {
      //   key: 'jteu',
      //   label: 'gCO₂ / JTEU × nm',
      //   visible: true,
      //   expanded: false,
      //   columns: [
      //     { key: 'jteu_j1', label: 'J1' },
      //     { key: `jteu_q1`, label: `${yr} Q1` },
      //     { key: `jteu_q2`, label: `${yr} Q2` },
      //     { key: `jteu_q3`, label: `${yr} Q3` },
      //     { key: `jteu_q4`, label: `${yr} Q4` },
      //     { key: `jteu_total`, label: `${yr} Total` }
      //   ]
      // },
      {
        key: 'tw',
        label: 'Transport Work (mt × nm)',
        visible: true,
        expanded: false,
        columns: [
          { key: `transport_work_q1`, label: `${yr} Q1` },
          { key: `transport_work_q2`, label: `${yr} Q2` },
          { key: `transport_work_q3`, label: `${yr} Q3` },
          { key: `transport_work_q4`, label: `${yr} Q4` },
          { key: `transport_work_total`, label: `${yr} Total` }
        ]
      }
    ];
  }


  columnGroups = [

    {
      key: 'eeoi',
      label: 'EEOI (g/mt × nm)',
      visible: true,
      expanded: false,
      columns: [
        { key: 'eeoi_2025_q1', label: '2025 Q1' },
        { key: 'eeoi_2025_q2', label: '2025 Q2' },
        { key: 'eeoi_2025_q3', label: '2025 Q3' },
        { key: 'eeoi_2025_q4', label: '2025 Q4' },
        {
          key: 'eeoi_2025_total',
          label: '2025 (Q1–Q4)',
          isTotal: true
        }
      ]
    },

    // 2️⃣ CO₂ Intensity Displacement
    // {
    //   key: 'co2int',
    //   label: 'CO₂ Intensity Displacement (g/mt × nm)',
    //   visible: true,
    //   columns: [
    //     { key: 'co2int_2025_q1', label: '2025 Q1' },
    //     { key: 'co2int_2025_q2', label: '2025 Q2' },
    //     { key: 'co2int_2025_q3', label: '2025 Q3' },
    //     { key: 'co2int_2025_q4', label: '2025 Q4' },
    //     { key: 'co2int_2025_total', label: '2025 (Q1–Q4)' },
    //   ]
    // },

    // 3️⃣ CO₂e
    {
      key: 'co2e',
      label: 'CO₂e (mt)',
      visible: true,
      columns: [
        { key: 'co2e_2025_q1', label: '2025 Q1' },
        { key: 'co2e_2025_q2', label: '2025 Q2' },
        { key: 'co2e_2025_q3', label: '2025 Q3' },
        { key: 'co2e_2025_q4', label: '2025 Q4' },
        { key: 'co2e_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 4️⃣ PM10
    {
      key: 'pm10',
      label: 'PM10 (kg)',
      visible: true,
      columns: [
        { key: 'pm10_2025_q1', label: '2025 Q1' },
        { key: 'pm10_2025_q2', label: '2025 Q2' },
        { key: 'pm10_2025_q3', label: '2025 Q3' },
        { key: 'pm10_2025_q4', label: '2025 Q4' },
        { key: 'pm10_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 5️⃣ PM10 Intensity
    {
      key: 'pm10_intensity',
      label: 'PM10 Intensity (mg/mt × nm)',
      visible: true,
      columns: [
        { key: 'pm10i_2025_q1', label: '2025 Q1' },
        { key: 'pm10i_2025_q2', label: '2025 Q2' },
        { key: 'pm10i_2025_q3', label: '2025 Q3' },
        { key: 'pm10i_2025_q4', label: '2025 Q4' },
        { key: 'pm10i_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 6️⃣ CH4
    {
      key: 'ch4',
      label: 'CH4 (kg)',
      visible: true,
      columns: [
        { key: 'ch4_2025_q1', label: '2025 Q1' },
        { key: 'ch4_2025_q2', label: '2025 Q2' },
        { key: 'ch4_2025_q3', label: '2025 Q3' },
        { key: 'ch4_2025_q4', label: '2025 Q4' },
        { key: 'ch4_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 7️⃣ CH4 Intensity
    {
      key: 'ch4_intensity',
      label: 'CH4 Intensity (mg/mt × nm)',
      visible: true,
      columns: [
        { key: 'ch4i_2025_q1', label: '2025 Q1' },
        { key: 'ch4i_2025_q2', label: '2025 Q2' },
        { key: 'ch4i_2025_q3', label: '2025 Q3' },
        { key: 'ch4i_2025_q4', label: '2025 Q4' },
        { key: 'ch4i_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 8️⃣ CO
    {
      key: 'co',
      label: 'CO (kg)',
      visible: true,
      columns: [
        { key: 'co_2025_q1', label: '2025 Q1' },
        { key: 'co_2025_q2', label: '2025 Q2' },
        { key: 'co_2025_q3', label: '2025 Q3' },
        { key: 'co_2025_q4', label: '2025 Q4' },
        { key: 'co_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 9️⃣ N₂O
    {
      key: 'n2o',
      label: 'N₂O (kg)',
      visible: true,
      columns: [
        { key: 'n2o_2025_q1', label: '2025 Q1' },
        { key: 'n2o_2025_q2', label: '2025 Q2' },
        { key: 'n2o_2025_q3', label: '2025 Q3' },
        { key: 'n2o_2025_q4', label: '2025 Q4' },
        { key: 'n2o_2025_total', label: '2025 (Q1–Q4)' },
      ]
    },

    // 🔟 gCO₂ / JTEU × nm
    // {
    //   key: 'jteu',
    //   label: 'gCO₂ / JTEU × nm',
    //   visible: true,
    //   columns: [
    //     { key: 'jteu_j1', label: 'J1' },
    //     { key: 'jteu_2025_q1', label: '2025 Q1' },
    //     { key: 'jteu_2025_q2', label: '2025 Q2' },
    //     { key: 'jteu_2025_q3', label: '2025 Q3' },
    //     { key: 'jteu_2025_q4', label: '2025 Q4' },
    //     { key: 'jteu_2025_total', label: '2025 (Q1–Q4)' },
    //   ]
    // },

    // 1️⃣1️⃣ Transport Work
    {
      key: 'tw',
      label: 'Transport Work (mt × nm)',
      visible: true,
      columns: [
        { key: 'tw_2025_q1', label: '2025 Q1' },
        { key: 'tw_2025_q2', label: '2025 Q2' },
        { key: 'tw_2025_q3', label: '2025 Q3' },
        { key: 'tw_2025_q4', label: '2025 Q4' },
        { key: 'tw_2025_total', label: '2025 (Q1–Q4)' },
      ]
    }

  ];


  get visibleGroups() {
    return this.columnGroups.filter(g => g.visible);
  }

  private isTotalColumnKey(key: string): boolean {
    return (key ?? '').toLowerCase().endsWith('_total');
  }

  /**
   * Collapsed mode: show ONLY the "total" sub-column(s).
   * Expanded mode: show ALL sub-columns.
   *
   * IMPORTANT: we use structural rendering (ngFor over this list)
   * instead of [hidden], so the header colspan and the body cells
   * stay perfectly aligned.
   */
  getVisibleColumnsForGroup(group: any): { key: string; label: string }[] {
    const cols = group?.columns ?? [];
    if (group?.expanded) return cols;

    const totals = cols.filter((c: { key: string }) => this.isTotalColumnKey(c.key));
    if (totals.length) return totals;

    // Fallback: if no explicit *_total column exists, show the last column.
    // This prevents “blank” groups when naming differs.
    return cols.length ? cols.slice(-1) : [];
  }


  tableData: any[] = [
    {
      ship_name: "-",
      ship_imo: "-",

      eeoi_2025_q1: 10.82, eeoi_2025_q2: 11.40, eeoi_2025_q3: 12.01, eeoi_2025_q4: 14.03, eeoi_2025_total: 48.26,
      co2int_2025_q1: 7.30, co2int_2025_q2: 6.47, co2int_2025_q3: 6.67, co2int_2025_q4: 7.54, co2int_2025_total: 28.14,
      co2e_2025_q1: 4200.62, co2e_2025_q2: 3770.12, co2e_2025_q3: 4558.63, co2e_2025_q4: 4870.22, co2e_2025_total: 17400.12,
      pm10_2025_q1: 112.2, pm10_2025_q2: 98.3, pm10_2025_q3: 103.1, pm10_2025_q4: 110.0, pm10_2025_total: 423.6,
      ch4_2025_q1: 22.8, ch4_2025_q2: 30.1, ch4_2025_q3: 27.9, ch4_2025_q4: 28.4, ch4_2025_total: 109.2,

      tw_2025_q1: 2488000, tw_2025_q2: 2640000, tw_2025_q3: 2985000, tw_2025_q4: 3210000, tw_2025_total: 11323000,
    },
    {
      ship_name: "-",
      ship_imo: "-",

      eeoi_2025_q1: 9.12, eeoi_2025_q2: 10.04, eeoi_2025_q3: 10.93, eeoi_2025_q4: 12.58, eeoi_2025_total: 42.67,
      co2int_2025_q1: 6.21, co2int_2025_q2: 6.89, co2int_2025_q3: 7.11, co2int_2025_q4: 7.93, co2int_2025_total: 28.14,
      co2e_2025_q1: 3801.2, co2e_2025_q2: 4022.1, co2e_2025_q3: 4211.8, co2e_2025_q4: 4522.7, co2e_2025_total: 16557.8,
      pm10_2025_q1: 101.2, pm10_2025_q2: 97.7, pm10_2025_q3: 102.3, pm10_2025_q4: 109.1, pm10_2025_total: 410.3,
      ch4_2025_q1: 19.4, ch4_2025_q2: 21.2, ch4_2025_q3: 22.7, ch4_2025_q4: 23.1, ch4_2025_total: 86.4,

      tw_2025_q1: 2100000, tw_2025_q2: 2400000, tw_2025_q3: 2600000, tw_2025_q4: 3000000, tw_2025_total: 10100000,
    }
  ];

  /*******************************************
   * COMPONENT INIT
   *******************************************/
  ngOnInit(): void {
    this.subusername = localStorage.getItem('loginPic') ?? '';
    // Subscribe to query parameters from E-dashboard cards
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.activeFilter = (params['filter'] || '').trim().toLowerCase();
      this.selectedClient = (params['client'] || 'ALL').trim();
      this.fetchReportTableData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchReportTableData() {
    this.isLoading = true;
    const year = this.myreportForm.get('selectedYear')?.value || new Date().getFullYear();

    this.requestFormService.getFleetEmissionSummary(year, this.selectedClient).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.success) {
          // Existing unfiltered assignment commented out as required:
          // this.elements = this.mapFleetData(res.data);
          // this.elements = this.filterAndMaskDemoVoyages(this.elements);
          // this.paginateData();

          const mapped = this.mapFleetData(res.data);
          const masked = this.filterAndMaskDemoVoyages(mapped);
          this.allElements = masked;

          this.applyFilter();
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.toastr.error("Failed to fetch emission summary");
        this.isLoading = false;
      }
    });
  }

  applyFilter() {
    let filtered = [...this.allElements];

    if (this.activeFilter === 'distance') {
      // High Performing card: CII rating A or B
      filtered = filtered.filter(v => {
        const rating = (v.rating || '').toUpperCase();
        return rating === 'A' || rating === 'B';
      });
    } else if (this.activeFilter === 'high-risk') {
      // High Risk card: CII rating D or E
      filtered = filtered.filter(v => {
        const rating = (v.rating || '').toUpperCase();
        return rating === 'D' || rating === 'E';
      });
    }

    this.elements = filtered;
    this.paginateData();
  }

  goToPreviousPage(): void {
    this.router.navigate(['/E-dashboard']);
  }

  mapFleetData(data: any[]): any[] {
    if (this.subusername?.toLowerCase() === 'grace') {
      const graceImos = ['9378876', '9399480', '9438406', '9432048'];
      data = data.filter(
        (v: any) => graceImos.includes(String(v.imo ?? v.ship_imo ?? v.imo_number ?? '').trim())
      );
    }
  
    return data.map(v => {
      const roundVal = (val: any) => {
        if (val === null || val === undefined || isNaN(val)) return '-';
        return Number(val).toFixed(4);
      };

      const row: any = {
        ship_name: v.ship_name,
        ship_imo: v.imo,
        segment: v.segment,
        capacity_dwt: roundVal(v.dwt),
        distance_travelled: roundVal(v.distance_travelled),
        required_cii: roundVal(v.required_cii),
        attained_cii: roundVal(v.attained_cii),
        rating: v.rating,

        // Annual totals
        eeoi_total: roundVal(v.eeoi),
        co2_intensity_displacement_total: roundVal(v.co2_intensity_displacement),
        co2e_mt_total: roundVal(v.co2e_mt),
        pm10_kg_total: roundVal(v.pm10_kg),
        pm10_intensity_total: roundVal(v.pm10_intensity),
        ch4_kg_total: roundVal(v.ch4_kg),
        ch4_intensity_total: roundVal(v.ch4_intensity),
        co_kg_total: roundVal(v.co_kg),
        n2o_kg_total: roundVal(v.n2o_kg),
        transport_work_total: roundVal(v.transport_work)
      };

      // Flatten quarterly stats
      if (v.quarterly_stats) {
        const qs = v.quarterly_stats;
        [1, 2, 3, 4].forEach(q => {
          const qKey = `q${q}`;
          const qData = qs[qKey];

          if (qData) {
            row[`eeoi_q${q}`] = roundVal(qData.eeoi);
            row[`co2_intensity_displacement_q${q}`] = roundVal(qData.co2_intensity_displacement);
            row[`co2e_mt_q${q}`] = roundVal(qData.co2e_mt);
            row[`pm10_kg_q${q}`] = roundVal(qData.pm10_kg);
            row[`pm10_intensity_q${q}`] = roundVal(qData.pm10_intensity);
            row[`ch4_kg_q${q}`] = roundVal(qData.ch4_kg);
            row[`ch4_intensity_q${q}`] = roundVal(qData.ch4_intensity);
            row[`co_kg_q${q}`] = roundVal(qData.co_kg);
            row[`n2o_kg_q${q}`] = roundVal(qData.n2o_kg);
            row[`transport_work_q${q}`] = roundVal(qData.transport_work);
          }
        });
      }
      return row;
    });
  }

  /*******************************************
   * TABLE ACTIONS
   *******************************************/
  paginateData() {
    const startIndex = this.currentPage * this.itemsPerPage;
    this.paginatedData = this.elements.slice(startIndex, startIndex + this.itemsPerPage);
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.paginateData();
  }

  sortData(sort: Sort) {
    const data = this.paginatedData.slice();
    if (!sort.active || sort.direction === '') {
      this.paginatedData = data;
      return;
    }
    const isAsc = sort.direction === 'asc';
    this.paginatedData = data.sort((a, b) => (a[sort.active] < b[sort.active] ? -1 : 1) * (isAsc ? 1 : -1));
  }

  showDetails(row: any) {
    const imo = row?.original_vessel_imo || row?.ship_imo || row?.imo || row?.imo_number || row?.vessel_imo;
    if (imo) {
      this.router.navigate(['/E-overview-table', imo]);
    } else {
      console.error('IMO number is missing from row data', row);
      this.toastr.warning('Vessel IMO is not available');
    }
  }

  /*******************************************
   * FILTER & EXPORT
   *******************************************/
  filterClick() {
    const vesselIMO = this.myreportForm.get('vesselIMO')?.value?.trim() || '';
    this.paginatedData = this.tableData.filter(row =>
      row.ship_imo.toString().includes(vesselIMO)
    );
  }

  clearClick() {
    this.myreportForm.reset();
    this.fetchReportTableData();
  }


  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }
  filterShipNames(event: any) {
    const input = event.target.value.toLowerCase();
    this.vesselNameSuggestions = this.vesselnameoptions.filter((item: any) =>
      item.ship_name.toLowerCase().includes(input)
    );
  }
  openColumnFilterDialog(): void {
    const popupColumns = this.columnGroups.map(group => ({
      label: group.label,
      visible: group.visible,
      fixed: false
    }));

    const dialogRef = this.dialog.open(ColumnFilterPopupComponent, {
      width: '450px',
      data: { columns: popupColumns }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        result.forEach((col: any) => {
          const grp = this.columnGroups.find(g => g.label === col.label);
          if (grp) grp.visible = col.visible;
        });
      }
    });
  }

  toggleGroupExpand(group: any) {
    group.expanded = !group.expanded;
  }
  toggleGroupVisibility(group: any) {
    group.visible = !group.visible;
  }



  printFinalReport(row: any) {
    console.log("Print report clicked for:", row.ship_name);
    this.toastr.info("Print function coming soon");
  }
  downloadClick() {
    const csvHeader = "Ship Name,Ship IMO";
    const csvRows = this.paginatedData
      .map(r => `${r.ship_name},${r.ship_imo}`)
      .join("\n");

    const csvContent = csvHeader + "\n" + csvRows;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "emission_overview_export.csv";
    a.click();
  }

  formatValue(value: any) {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (!isNaN(num) && num === 0) return '-';
    return !isNaN(num)
      ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(num)
      : value;
  }

  private isDemoUser(): boolean {
    const username = (localStorage.getItem('username') || '').toLowerCase();
    const loginPic = (localStorage.getItem('loginPic') || '').toLowerCase();
    const subuser = (this.subusername || '').toLowerCase();
    return subuser === 'demo' || loginPic === 'demo' || username === 'meraki - demo' || username.includes('demo');
  }
  
  private filterAndMaskDemoVoyages(voyages: any[]): any[] {
    if (!this.isDemoUser()) {
      return voyages;
    }
  
    return (voyages || [])
      .filter((v: any) => {
        const imo = String(
          v?.imo_number ??
          v?.ship_imo ??
          v?.imo ??
          ''
        ).trim();
  
        return DEMO_VESSEL_PC.includes(imo);
      })
      .map((v: any) => {
        const imo = String(
          v?.imo_number ??
          v?.ship_imo ??
          v?.imo ??
          ''
        ).trim();
  
        const index = DEMO_VESSEL_PC.indexOf(imo);
  
        return {
          ...v,
           original_vessel_imo: v.ship_imo,
           original_vessel_name: v.ship_name,
          // Mask vessel name
          vessel_name: `Vessel ${index + 1}`,
          ship_name: `Vessel ${index + 1}`,
          client_name: 'Client 1',
          // Mask IMO
          imo: 'xxxxxxx',
          ship_imo: 'xxxxxxx',
        };
      });
  }
}
